import React, { useCallback, useState, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { AxiosResponse } from 'axios';
import {
	CONSTANTS,
	MAILING_LIST_ACCEPT_TYPES,
	MAILING_LIST_ACCEPT_TYPE_LABELS,
	MAILING_LIST_MAX_SIZE,
} from 'utils';
import {
	deleteFileFromDB,
	validateMailingList,
	isFileSizeValid,
	isFileFormatValid,
	mailingListsTableHeaderRow,
	renderMailingListsTableRow,
} from 'helpers';
import { IFile, IMailingFile, IMailingList } from 'modules/admin-panel/models';
import {
	IClientFormFields,
	ICompanyMailingList,
} from 'modules/admin-panel/pages/clients/models';
import {
	clientDataSelector,
	clientMailingListSelector,
} from 'modules/admin-panel/pages/clients/selectors';
import { Dropzone, SingleUpload } from './Dropzone';

const mailingListPath = '/clients/file/mailing-list/validate';

interface MailingListDropzoneProps {
	onUploadFail: (e: Error | null, message?: string | JSX.Element) => void;
	onUpload: (name: string, logo?: IFile | File) => void;
	uploadedData?: IFile;
}

export function MailingListDropzone({
	onUploadFail,
	onUpload,
	uploadedData,
}: MailingListDropzoneProps) {
	const { t } = useTranslation('common');
	const { t: te } = useTranslation('errors');
	const clientData = useSelector(clientDataSelector);
	const mailingLists = useSelector(clientMailingListSelector);
	const clientId = clientData?.id;
	const existingMailingLists = mailingLists?.length ? mailingLists : null;

	const { setValue, setError, clearError } = useFormContext();
	const [mailingListData, setMailingListData] = useState<ICompanyMailingList>();

	const onChangeMailingList = useCallback(
		(newMailingList?: File) => {
			setValue(IClientFormFields.mailingListKey, newMailingList?.name);
			onUpload(IClientFormFields.mailingListKey, newMailingList);
		},
		[setValue, onUpload],
	);

	const handleUploadMailingList = (mailingList: File) => {
		clearError(IClientFormFields.mailingListKey);
		if (!isFileSizeValid(mailingList.size, MAILING_LIST_MAX_SIZE)) {
			return setError(
				IClientFormFields.mailingListKey,
				'custom',
				te('file_size_not_more', {
					size: MAILING_LIST_MAX_SIZE,
				}),
			);
		}

		if (!isFileFormatValid(mailingList.name, MAILING_LIST_ACCEPT_TYPE_LABELS)) {
			return setError(
				IClientFormFields.mailingListKey,
				'custom',
				te('format_not_supported'),
			);
		}

		validateMailingList(mailingList, mailingListPath, 'file', clientId)
			.then((res: AxiosResponse<IMailingFile>) => {
				const {
					fileName,
					invalidUrl,
					employeesCount,
					newEmployeesCount,
					totalEmployeesCount,
				} = res.data;

				if (invalidUrl) {
					onUploadFail(
						null,
						<>
							{te('partially_uploaded')}
							<br />
							<a className="link" href={res.data?.invalidUrl} download>
								{t('invalid_data')}
							</a>
						</>,
					);
				}

				setMailingListData({
					mailingListKey: fileName,
					employeesCount,
					newEmployeesCount,
					totalEmployeesCount,
					dateAdded: new Date().toISOString(),
				});
				onChangeMailingList(mailingList);
			})
			.catch((e) => {
				onChangeMailingList();
				// check error code or message
				// setError(e.code);
				onUploadFail(e);
			});
	};

	const handleDeleteMailingList = useCallback(
		(mailingList: IFile) => {
			deleteFileFromDB(CONSTANTS.deleteFilePath, mailingList)
				.then(() => onChangeMailingList())
				.catch((e) => onUploadFail(e));
		},
		[onChangeMailingList, onUploadFail],
	);

	const onClickDeleteMailingList = useCallback(() => {
		handleDeleteMailingList(uploadedData!);
	}, [handleDeleteMailingList, uploadedData]);

	const existingMailingListsTableRows = useMemo(
		() =>
			existingMailingLists?.map((list: ICompanyMailingList) =>
				renderMailingListsTableRow(list, t),
			),
		[existingMailingLists, t],
	);

	const renderUploadedData = useCallback(
		(newMailingList?: IFile | any): JSX.Element | null => {
			if (!existingMailingListsTableRows && !newMailingList) return null;

			return (
				<table className="mailing-list__table">
					<thead>{mailingListsTableHeaderRow}</thead>
					<tbody>
						{existingMailingListsTableRows}
						{newMailingList &&
							mailingListData &&
							renderMailingListsTableRow(
								mailingListData,
								t,
								onClickDeleteMailingList,
							)}
					</tbody>
				</table>
			);
		},
		[
			t,
			mailingListData,
			existingMailingListsTableRows,
			onClickDeleteMailingList,
		],
	);

	return (
		<Dropzone<SingleUpload<IMailingList>>
			id={IClientFormFields.mailingListKey}
			renderUploadedData={renderUploadedData}
			acceptTypes={MAILING_LIST_ACCEPT_TYPES}
			acceptTypesLabels={MAILING_LIST_ACCEPT_TYPE_LABELS}
			onUpload={handleUploadMailingList}
			uploadedData={uploadedData}
			onDeleteUploadedData={handleDeleteMailingList}
			title={t('dropzone.text_file')}
			renderExistingFiles={!!existingMailingLists}
			closeIconClass="display-none"
		/>
	);
}
